import { is, patterns } from "quasar";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import { isValidPhoneNumber } from "./customValidators";

const { testPattern } = patterns;

export const required = (validations) => {
  return (value) => {
    if (typeof value === "string") {
      value = value.trim();
    }

    if (typeof value === "boolean") {
      return [true, false].includes(value);
    }

    return (
      (!!value && !Array.isArray(value) && !is.object(value)) ||
      (is.object(value) && value[validations?.key || "_id"] != null) ||
      (Array.isArray(value) && value.length > 0) ||
      (typeof value === "number" && value === 0) ||
      false
    );
  };
};

export const length = (validations) => {
  return (value) =>
    value == null ||
    Number.isNaN(Number(validations.length)) ||
    (typeof value === "string" && value === "") ||
    String(value).length === validations.length ||
    useChangeCase(
      `${validations.label} should be of ${validations.length} ${
        typeof value === "number" ? "digits" : "chars"
      }`,
      "sentenceCase",
    ).value;
};

export const minLength = (validations) => {
  return (value) =>
    value == null ||
    Number.isNaN(Number(validations.minLength)) ||
    (typeof value === "string" && value === "") ||
    String(value).length >= validations.minLength ||
    useChangeCase(
      `${validations.label} should be greater than or equal to ${
        validations.minLength
      } ${typeof value === "number" ? "digits" : "characters"}`,
      "sentenceCase",
    ).value;
};

export const maxLength = (validations) => {
  return (value) =>
    value == null ||
    Number.isNaN(Number(validations.maxLength)) ||
    (typeof value === "string" && value === "") ||
    String(value).length <= validations.maxLength ||
    useChangeCase(
      `${validations.label} should be less than or equal to ${
        validations.maxLength
      } ${typeof value === "number" ? "digits" : "characters"}`,
      "sentenceCase",
    ).value;
};

export const lengths = (validations) => {
  return (value) =>
    value == null ||
    !Array.isArray(validations.lengths) ||
    (typeof value === "string" && value === "") ||
    (String(value).length >= validations.lengths[0] &&
      String(value).length <= validations.lengths[1]) ||
    useChangeCase(
      `${validations.label} should be between ${validations.lengths[0]} to ${
        validations.lengths[1]
      } ${typeof value === "number" ? "digits" : "characters"}`,
      "sentenceCase",
    ).value;
};

export const integer = (validations) => {
  return (value) =>
    value === 0 ||
    value === "" ||
    value == null ||
    (Number(value) && Number.isSafeInteger(Number(value))) ||
    useChangeCase(
      `${validations.label} should be a valid number`,
      "sentenceCase",
    ).value;
};

export const float = (validations) => {
  return (value) =>
    value === 0 ||
    (Number(value) && Number(value) < Number.MAX_SAFE_INTEGER) ||
    useChangeCase(
      `${validations.label} should be a valid number`,
      "sentenceCase",
    ).value;
};

export const gt = (validations) => {
  return (value) =>
    Number.isNaN(Number(validations.gt)) ||
    value > validations.gt ||
    useChangeCase(
      `${validations.label} should be greater than ${validations.gt}`,
      "sentenceCase",
    ).value;
};

export const lt = (validations) => {
  return (value) =>
    Number.isNaN(Number(validations.lt)) ||
    value < validations.lt ||
    useChangeCase(
      `${validations.label} should be less than ${validations.lt}`,
      "sentenceCase",
    ).value;
};

export const gte = (validations) => {
  return (value) =>
    value == null ||
    Number.isNaN(Number(validations.gte)) ||
    value >= validations.gte ||
    useChangeCase(`${validations.label} should be min`, "sentenceCase").value +
      " " +
      validations.gte;
};

export const lte = (validations) => {
  return (value) =>
    value == null ||
    Number.isNaN(Number(validations.lte)) ||
    value <= validations.lte ||
    useChangeCase(
      `${validations.label} should be max ${validations.lte}`,
      "sentenceCase",
    ).value;
};

export const phoneNumber = () => {
  return (value) => !value || isValidPhoneNumber(value) || false;
};

export const email = () => {
  return (value) => !value || testPattern.email(value) || false;
};

export const gstin = () => {
  return (value) =>
    !value || /^\d{2}[A-Z]{5}\d{4}[A-Z][A-Z\d]Z[A-Z\d]$/.test(value) || false;
};

export const pan = () => {
  return (value) => !value || /^[A-Z]{5}\d{4}[A-Z]$/.test(value) || false;
};

export const ifsc = () => {
  const regex = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;

  return (value) => regex.test(value) || "Invalid IFSC";
};
